import {
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import * as calc from '../engine';
import { SWR_DEFAULT } from '../constants/financial';
import { DEF } from '../constants/defaults';
import { F, FF, P } from '../utils/format';
import { Card } from '../components/Card';
import { Heading } from '../components/Heading';
import { Slider } from '../components/Slider';
import { Table } from '../components/Table';
import { Grid } from '../components/Grid';
import { Stack } from '../components/Stack';
import { Target, DollarSign, TrendingUp, Activity, Save, Play, Plus, Trash } from '../components/icons';
import { useApp } from '../hooks/useApp';
import InsightBanner from '../features/motivation/InsightBanner';
import { projectionInsights } from '../features/motivation/motivationEngine';

export default function Projections() {
  const { s, c, proj, pF, p0, up, upM, setS, tt, fiAge, fiReached, coastAge, budgetMonthly, blendedReturn, realReturn, amor, scen, mcData, mcRunning, runMonteCarlo, showToast, setConfirm, fileRef } = useApp();
  const sc = scen || calc.scen(s);
  const insights = projectionInsights(s, proj, pF, fiReached, fiAge, scen);
  const merged = sc.base.map((d, i) => ({
    a: d.a, base: d.nw,
    bull: sc.bull[i]?.nw || 0,
    bear: sc.bear[i]?.nw || 0,
  }));
  const fBu = sc.bull[sc.bull.length - 1]?.nw || 0;
  const fBe = sc.bear[sc.bear.length - 1]?.nw || 0;
  const scenarios = s.scenarios || [];

  return (
    <Stack>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 }}>
        <Card c={c} delay={0} sx={{ padding: 18 }}>
          <div style={{ fontSize: 11, color: c.red, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Bear Case</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: c.fg, marginTop: 5 }}>{F(fBe)}</div>
        </Card>
        <Card glow c={c} delay={0.04} sx={{ padding: 18 }}>
          <div style={{ fontSize: 11, color: c.blue, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Base Case</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: c.fg, marginTop: 5 }}>{F(pF.nw)}</div>
        </Card>
        <Card c={c} delay={0.08} sx={{ padding: 18 }}>
          <div style={{ fontSize: 11, color: c.green, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>Bull Case</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: c.fg, marginTop: 5 }}>{F(fBu)}</div>
        </Card>
      </div>

      <InsightBanner insights={insights} />

      <Grid>
        <Card c={c} delay={0.1}>
          <Heading ic={Target} c={c}>What-If Engine</Heading>
          <Slider l="Return Rate" v={s.ret} fn={(v) => up("ret", v)} min={0.04} max={0.15} step={0.005} f="%" c={c} />
          <Slider l="Salary Growth" v={s.salGr} fn={(v) => up("salGr", v)} min={0.02} max={0.12} step={0.005} f="%" c={c} />
          <Slider l="Living Expenses" v={s.budgetSync ? budgetMonthly * 12 : s.liv} fn={(v) => { if (!s.budgetSync) up("liv", v); }} min={20000} max={80000} step={1000} f="$" c={c} />
          {s.budgetSync && <div style={{ fontSize: 11, color: c.muted, marginTop: -12, marginBottom: 8 }}>Synced from Budget tab</div>}
          <Slider l="Appreciation" v={s.hApp} fn={(v) => up("hApp", v)} min={0.01} max={0.08} step={0.005} f="%" c={c} />
          <Slider l="Mortgage Rate" v={s.mortRate} fn={(v) => up("mortRate", v)} min={0.02} max={0.08} step={0.001} f="%" c={c} />
          <Slider l="Inflation" v={s.inflation != null ? s.inflation : 0.025} fn={(v) => up("inflation", v)} min={0} max={0.06} step={0.005} f="%" c={c} />
          <Slider l="Plan To Age" v={s.maxAge || 40} fn={(v) => up("maxAge", v)} min={35} max={65} c={c} />
        </Card>
        <Card c={c} delay={0.14}>
          <Heading ic={DollarSign} c={c}>Safe Withdrawal Rates</Heading>
          <Table h={["SWR", "Bear", "Base", "Bull"]} r={[0.03, 0.035, 0.04, 0.045, 0.05].map((r) => [P(r), FF(fBe * r), FF(pF.nw * r), FF(fBu * r)])} c={c} />
          <div style={{
            padding: "12px 16px", borderRadius: 12, background: c.card2,
            fontSize: 13, marginTop: 14, display: "flex", justifyContent: "space-between",
          }}>
            <span style={{ color: c.muted }}>4% SWR covers</span>
            <span style={{ color: c.green, fontWeight: 700 }}>
              {((pF.nw * SWR_DEFAULT) / (pF.liv || s.liv)).toFixed(1)}× expenses
            </span>
          </div>
        </Card>
      </Grid>

      {/* Scenario Library */}
      <Card c={c} delay={0.16}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
          <Heading ic={Save} c={c}>Scenario Library</Heading>
          <button onClick={() => {
            const name = prompt("Scenario name:");
            if (!name) return;
            const newScen = { name, state: { ...s, scenarios: undefined }, nw: pF.nw, ts: Date.now() };
            up("scenarios", [...scenarios.slice(0, 4), newScen]);
            showToast(`Saved "${name}"`, "success");
          }} style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "7px 16px", borderRadius: 10, border: `1px solid ${c.border}`,
            background: "transparent", color: c.blue, fontSize: 12, fontWeight: 600, cursor: "pointer",
          }}>
            <Plus size={12} /> Save Current
          </button>
        </div>
        {scenarios.length === 0 && (
          <div style={{ fontSize: 13, color: c.muted, padding: "16px 0" }}>No saved scenarios yet. Save your current assumptions to compare different strategies.</div>
        )}
        {scenarios.map((sc, i) => (
          <div key={i} style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "12px 0", borderBottom: i < scenarios.length - 1 ? `1px solid ${c.border}` : "none",
          }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: c.fg }}>{sc.name}</div>
              <div style={{ fontSize: 12, color: c.muted }}>NW at target: {F(sc.nw)}</div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => { setS({ ...DEF, ...sc.state, scenarios }); showToast(`Loaded "${sc.name}"`, "info"); }}
                style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${c.border}`, background: "transparent", color: c.blue, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                Load
              </button>
              <button onClick={() => { up("scenarios", scenarios.filter((_, j) => j !== i)); showToast("Deleted", "info"); }}
                style={{ padding: "5px 8px", borderRadius: 8, border: `1px solid ${c.border}`, background: "transparent", color: c.red, fontSize: 12, cursor: "pointer" }}>
                <Trash size={12} />
              </button>
            </div>
          </div>
        ))}
      </Card>

      <Card c={c} delay={0.18}>
        <Heading ic={TrendingUp} c={c}>Scenario Comparison</Heading>
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart data={merged}>
            <defs>
              <linearGradient id="gBase" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={c.blue} stopOpacity={0.15} />
                <stop offset="100%" stopColor={c.blue} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={c.border} />
            <XAxis dataKey="a" tick={{ fontSize: 12, fill: c.muted }} axisLine={{ stroke: c.border }} tickLine={false} />
            <YAxis tickFormatter={F} tick={{ fontSize: 12, fill: c.muted }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v) => FF(v)} {...tt} />
            <Area type="monotone" dataKey="bear" stroke={c.muted} strokeWidth={1.5} fill="transparent" strokeDasharray="5 3" name="Bear" opacity={0.6} />
            <Area type="monotone" dataKey="base" stroke={c.blue} strokeWidth={2.5} fill="url(#gBase)" name="Base" />
            <Area type="monotone" dataKey="bull" stroke={c.green} strokeWidth={1.5} fill="transparent" strokeDasharray="5 3" name="Bull" opacity={0.6} />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      {/* Monte Carlo */}
      <Card c={c} delay={0.22}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
          <Heading ic={Activity} c={c}>Monte Carlo Simulation</Heading>
          <button onClick={runMonteCarlo} disabled={mcRunning}
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              padding: "7px 16px", borderRadius: 10, border: "none",
              background: c.blue, color: "#fff", fontSize: 12, fontWeight: 600,
              cursor: mcRunning ? "wait" : "pointer", opacity: mcRunning ? 0.6 : 1,
            }}>
            <Play size={12} /> {mcRunning ? "Running..." : "Run 500 Simulations"}
          </button>
        </div>
        {mcData ? (
          <>
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              {[
                { l: "10th %ile", v: mcData[mcData.length - 1]?.p10, cl: c.red },
                { l: "Median", v: mcData[mcData.length - 1]?.p50, cl: c.blue },
                { l: "90th %ile", v: mcData[mcData.length - 1]?.p90, cl: c.green },
              ].map(x => (
                <div key={x.l} style={{ flex: "1 1 100px", padding: "10px 14px", borderRadius: 12, background: c.card2 }}>
                  <div style={{ fontSize: 11, color: c.muted, fontWeight: 600, textTransform: "uppercase" }}>{x.l}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: x.cl, marginTop: 3 }}>{F(x.v)}</div>
                </div>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={mcData}>
                <defs>
                  <linearGradient id="gMC" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={c.blue} stopOpacity={0.1} />
                    <stop offset="100%" stopColor={c.blue} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={c.border} />
                <XAxis dataKey="a" tick={{ fontSize: 12, fill: c.muted }} axisLine={{ stroke: c.border }} tickLine={false} />
                <YAxis tickFormatter={F} tick={{ fontSize: 12, fill: c.muted }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => FF(v)} {...tt} />
                <Area type="monotone" dataKey="p90" stroke={c.green} fill="transparent" strokeWidth={1.5} strokeDasharray="4 2" name="90th" opacity={0.5} />
                <Area type="monotone" dataKey="p75" stroke="transparent" fill={c.blue} fillOpacity={0.06} name="75th" />
                <Area type="monotone" dataKey="p50" stroke={c.blue} fill="url(#gMC)" strokeWidth={2.5} name="Median" />
                <Area type="monotone" dataKey="p25" stroke="transparent" fill={c.blue} fillOpacity={0.06} name="25th" />
                <Area type="monotone" dataKey="p10" stroke={c.red} fill="transparent" strokeWidth={1.5} strokeDasharray="4 2" name="10th" opacity={0.5} />
              </AreaChart>
            </ResponsiveContainer>
          </>
        ) : (
          <div style={{ fontSize: 13, color: c.muted, padding: "20px 0", textAlign: "center" }}>
            Run a simulation to see probabilistic outcomes across 500 randomized paths.
          </div>
        )}
      </Card>
    </Stack>
  );
}
